<template>
  <div class="ts-wrapper">

    <!-- HEADER -->
    <div class="ts-header">
      <div class="ts-header__left">
        <div class="ts-header__icon">
          <v-icon size="14" color="#3C5AAA">mdi-table-clock</v-icon>
        </div>
        <span class="ts-header__title">Time Sheets</span>
      </div>
      <v-menu location="bottom end" :close-on-content-click="true">
        <template v-slot:activator="{ props }">
          <button class="ts-export-btn" v-bind="props">
            <v-icon size="13" color="#3C5AAA">mdi-export-variant</v-icon>
            <span>Export</span>
            <v-icon size="12" color="#3C5AAA">mdi-chevron-down</v-icon>
          </button>
        </template>
        <div class="ts-export-menu">
          <div class="ts-export-menu__header">
            <v-icon size="11" color="#6366f1">mdi-download-outline</v-icon>
            <span>Download as</span>
          </div>
          <div class="ts-export-menu__divider" />
          <button
            v-for="(item, index) in downloadMenu"
            :key="index"
            class="ts-export-menu__item"
            @click="exportFile(item)"
          >
            <span class="ts-export-menu__icon-wrap">
              <v-icon size="13" :color="exportIconColor(item)">{{ exportIcon(item) }}</v-icon>
            </span>
            <span class="ts-export-menu__label">{{ item }}</span>
            <v-icon size="11" color="#cbd5e1" class="ts-export-menu__arrow">mdi-arrow-right</v-icon>
          </button>
        </div>
      </v-menu>
    </div>

    <!-- TABS -->
    <div class="ts-tabs">
      <button
        class="ts-tab"
        :class="{ 'ts-tab--active': tab === 'tab-1' }"
        @click="tab = 'tab-1'"
      >
        <v-icon size="12">mdi-calendar-month-outline</v-icon>
        By Date
      </button>
      <button
        class="ts-tab"
        :class="{ 'ts-tab--active': tab === 'tab-2' }"
        @click="tab = 'tab-2'"
      >
        <v-icon size="12">mdi-domain</v-icon>
        By Account
      </button>
    </div>

    <!-- LOADER -->
    <div class="ts-loader" v-if="loading">
      <div class="ts-shimmer-wrap">
        <!-- Fake thead -->
        <div class="ts-shimmer ts-shimmer--thead"></div>
        <!-- Fake group label -->
        <div class="ts-shimmer ts-shimmer--group"></div>
        <!-- Fake rows -->
        <div class="ts-shimmer-row" v-for="n in 4" :key="n">
          <div class="ts-shimmer ts-shimmer--cell ts-shimmer--chip"></div>
          <div class="ts-shimmer ts-shimmer--cell ts-shimmer--time"></div>
          <div class="ts-shimmer ts-shimmer--cell ts-shimmer--ref"></div>
          <div class="ts-shimmer ts-shimmer--cell ts-shimmer--cat"></div>
          <div class="ts-shimmer ts-shimmer--cell ts-shimmer--dur"></div>
        </div>
        <!-- Second group -->
        <div class="ts-shimmer ts-shimmer--group ts-shimmer--w50"></div>
        <div class="ts-shimmer-row" v-for="n in 2" :key="'b'+n">
          <div class="ts-shimmer ts-shimmer--cell ts-shimmer--chip"></div>
          <div class="ts-shimmer ts-shimmer--cell ts-shimmer--time"></div>
          <div class="ts-shimmer ts-shimmer--cell ts-shimmer--ref"></div>
          <div class="ts-shimmer ts-shimmer--cell ts-shimmer--cat"></div>
          <div class="ts-shimmer ts-shimmer--cell ts-shimmer--dur"></div>
        </div>
      </div>
    </div>

    <!-- CONTENT -->
    <div class="ts-content" v-else>

      <!-- BY DATE -->
      <div v-if="tab === 'tab-1'">
        <div v-if="timeData.byDate && timeData.byDate.length > 0">
          <!-- Table header -->
          <div class="ts-scroll-wrap">
            <div class="ts-table ts-table--no-date">
              <div class="ts-thead">
                <div class="ts-th ts-col-type">Type</div>
                <div class="ts-th ts-col-time">Start → End</div>
                <div class="ts-th ts-col-ref">Reference</div>
                <div class="ts-th ts-col-cat">Category</div>
                <div class="ts-th ts-col-shif">Shift</div>
                <div class="ts-th ts-col-dur">Duration</div>
              </div>
              <template v-for="(item, index) in timeData.byDate" :key="index">
                <!-- Group header row -->
                <div class="ts-group-row">
                  <div class="ts-group-row__left">
                    <v-icon size="12" color="#3C5AAA">mdi-calendar-today</v-icon>
                    <span>{{ dayjs(item.entry_date_ist).utc().format("MMM DD, YYYY") }}</span>
                  </div>
                  <span class="ts-group-total">
                    <v-icon size="11" color="#3C5AAA">mdi-clock-outline</v-icon>
                    {{ item && item.total_hrs ? item.total_hrs.toFixed(2) : 0 }} min
                  </span>
                </div>
                <!-- Data rows -->
                <div
                  class="ts-row"
                  v-for="(value, i) in JSON.parse(item.groupData)"
                  :key="i"
                >
                  <div class="ts-col-type">
                    <v-tooltip location="top" :text="value.tasks || value.late_type || ''" open-delay="200">
                      <template #activator="{ props }">
                        <span class="ts-chip-wrap" v-bind="props">
                          <span class="ts-chip" :style="{ background: getBgColors(value.tasks), color: getColors(value.tasks) }">
                            <span class="ts-chip-label">{{ value.tasks ? value.tasks.toUpperCase() : value.late_type ? value.late_type.toUpperCase() : '' }}</span>
                          </span>
                        </span>
                      </template>
                    </v-tooltip>
                  </div>
                  <div class="ts-col-time ts-cell-time">
                    {{ moment(value.startDate).format("hh:mm A") !== "Invalid date" ? moment(value.startDate).format("hh:mm A") : "--" }}
                    <span class="ts-arrow">→</span>
                    {{ moment(value.endDate).format("hh:mm A") !== "Invalid date" ? moment(value.endDate).format("hh:mm A") : "--" }}
                  </div>
                  <div class="ts-col-ref">
                    <span class="ts-ref-badge" v-if="value.issueRefNum">{{ value.issueRefNum }}</span>
                    <span v-else class="ts-cell-muted">—</span>
                  </div>
                  <div class="ts-col-cat">
                    <v-tooltip v-if="value.categoryName" location="top" :text="[value.categoryName, value.subCategoryName].filter(Boolean).join(' › ')" open-delay="200">
                      <template #activator="{ props }">
                        <span class="ts-cat-wrap" v-bind="props">
                          <span class="ts-cell-text" v-if="value.categoryName">{{ value.categoryName }}</span>
                          <span v-if="value.subCategoryName" class="ts-sub-chip" :style="getSubCatStyle(value.subCategoryName)">
                            <span class="ts-chip-label">{{ value.subCategoryName }}</span>
                          </span>
                        </span>
                      </template>
                    </v-tooltip>
                    <span v-if="!value.categoryName" class="ts-cell-muted">—</span>
                  </div>
                  <div class="ts-col-shif">
                    <v-tooltip v-if="value.shift_name" location="top" :text="value.shift_name" open-delay="200">
                      <template #activator="{ props }">
                        <span class="ts-cell-text" v-bind="props">{{ value.shift_name }}</span>
                      </template>
                    </v-tooltip>
                    <span v-else class="ts-cell-muted">—</span>
                  </div>
                  <div class="ts-col-dur">
                    <span class="ts-dur-val">{{ value && value.actualHrs ? value.actualHrs.toFixed(2) : 0 }}</span>
                    <span class="ts-dur-unit">min</span>
                  </div>
                </div>
              </template>
            </div>
          </div>
        </div>
        <div class="ts-empty" v-else>
          <img :src="require('@/assets/no-data-found.svg')" class="ts-empty__img" alt="No data" />
          <span class="ts-empty__title">No Timesheet Data</span>
          <span class="ts-empty__sub">No timesheet entries found for the selected date range.</span>
        </div>
      </div>

      <!-- BY ACCOUNT -->
      <div v-if="tab === 'tab-2'">
        <div v-if="timeData.byAccounts && timeData.byAccounts.length > 0">
          <div class="ts-scroll-wrap">
            <div class="ts-table">
              <div class="ts-thead">
                <div class="ts-th ts-col-type">Type</div>
                <div class="ts-th ts-col-date">Account</div>
                <div class="ts-th ts-col-time">Start → End</div>
                <div class="ts-th ts-col-ref">Reference</div>
                <div class="ts-th ts-col-cat">Category</div>
                <div class="ts-th ts-col-shif">Shift</div>
                <div class="ts-th ts-col-dur">Duration</div>
              </div>
              <template v-for="(item, index) in timeData.byAccounts" :key="index">
                <div class="ts-group-row">
                  <div class="ts-group-row__left">
                    <v-icon size="12" color="#3C5AAA">mdi-domain</v-icon>
                    <span>{{ item.account_name }}</span>
                  </div>
                  <span class="ts-group-total">
                    <v-icon size="11" color="#3C5AAA">mdi-clock-outline</v-icon>
                    {{ item && item.total_hrs ? item.total_hrs.toFixed(2) : 0 }} min
                  </span>
                </div>
                <div
                  class="ts-row"
                  v-for="(value, i) in JSON.parse(item.groupData)"
                  :key="i"
                >
                  <div class="ts-col-type">
                    <v-tooltip location="top" :text="value.tasks || ''" open-delay="200">
                      <template #activator="{ props }">
                        <span class="ts-chip-wrap" v-bind="props">
                          <span class="ts-chip" :style="{ background: getBgColors(value.tasks), color: getColors(value.tasks) }">
                            <span class="ts-chip-label">{{ value.tasks ? value.tasks.toUpperCase() : '' }}</span>
                          </span>
                        </span>
                      </template>
                    </v-tooltip>
                  </div>
                  <div class="ts-col-date ts-cell-text">{{ item.account_name }}</div>
                  <div class="ts-col-time ts-cell-time">
                    {{ moment(value.startDate).format("hh:mm A") !== "Invalid date" ? moment(value.startDate).format("hh:mm A") : "--" }}
                    <span class="ts-arrow">→</span>
                    {{ moment(value.endDate).format("hh:mm A") !== "Invalid date" ? moment(value.endDate).format("hh:mm A") : "--" }}
                  </div>
                  <div class="ts-col-ref">
                    <span class="ts-ref-badge" v-if="value.issueRefNum">{{ value.issueRefNum }}</span>
                    <span v-else class="ts-cell-muted">—</span>
                  </div>
                  <div class="ts-col-cat">
                    <v-tooltip v-if="value.categoryName" location="top" :text="[value.categoryName, value.subCategoryName].filter(Boolean).join(' › ')" open-delay="200">
                      <template #activator="{ props }">
                        <span class="ts-cat-wrap" v-bind="props">
                          <span class="ts-cell-text" v-if="value.categoryName">{{ value.categoryName }}</span>
                          <span v-if="value.subCategoryName" class="ts-sub-chip" :style="getSubCatStyle(value.subCategoryName)">
                            <span class="ts-chip-label">{{ value.subCategoryName }}</span>
                          </span>
                        </span>
                      </template>
                    </v-tooltip>
                    <span v-if="!value.categoryName" class="ts-cell-muted">—</span>
                  </div>
                  <div class="ts-col-shif">
                    <v-tooltip v-if="value.shift_name" location="top" :text="value.shift_name" open-delay="200">
                      <template #activator="{ props }">
                        <span class="ts-cell-text" v-bind="props">{{ value.shift_name }}</span>
                      </template>
                    </v-tooltip>
                    <span v-else class="ts-cell-muted">—</span>
                  </div>
                  <div class="ts-col-dur">
                    <span class="ts-dur-val">{{ value && value.actualHrs ? value.actualHrs.toFixed(2) : 0 }}</span>
                    <span class="ts-dur-unit">min</span>
                  </div>
                </div>
              </template>
            </div>
          </div>
        </div>
        <div class="ts-empty" v-else>
          <img :src="require('@/assets/no-data-found.svg')" class="ts-empty__img" alt="No data" />
          <span class="ts-empty__title">No Timesheet Data</span>
          <span class="ts-empty__sub">No timesheet entries found for the selected account.</span>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
import moment from "moment";
import commonService from "@/common.service";
import ExportService from "@/ExportService";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import TimeSheetService from "@/api/service/TimeSheet/TimeSheetService";

export default {
  props: ["timeSheetData", "userId", "fromDate", "toDate"],
  data() {
    return {
      timeData: [],
      loading: false,
      tab: "tab-1",
      columns: null,
      downloadMenu: ["Excel", "Pdf", "Json"],
    };
  },
  watch: {
    reloadValue() {
      this.getTimeData(this.userId);
    },
    fromDate() {
      if (this.fromDate) this.getTimeData(this.userId);
    },
    toDate() {
      if (this.toDate) this.getTimeData(this.userId);
    },
    timeSheetData() {
      if (this.timeSheetData) {
        console.log(this.timeSheetData, "alert3333");
      }
    },
    userId() {
      if (this.userId) this.getTimeData(this.userId);
    },
    getRefresh() {
      this.getTimeData(this.userId);
    },
  },
  mounted() {
    console.log(this.timeSheetData, "kkkkkaaaasss");
  },
  created() {
    this.getLayoutColumn();
    dayjs.extend(utc);
    this.getTimeData(this.userId);
  },
  computed: {
    reloadValue() {
      return this.$store.state.hideTimer;
    },
    getRefresh() {
      return this.$store.getters.getRefreshTimeSheet;
    },
  },
  methods: {
    moment,
    dayjs,
    getLayoutColumn() {
      this.columns = [
        { field: "account_name", header: "Account", style: { width: "100px", color: "" }, toolTip: "Name", sortfield: true },
        { field: "issueRefNum", header: "Reference No", style: { width: "100px", color: "" }, toolTip: "Reporting", sortfield: true },
        { field: "categoryName", header: "Category", style: { width: "100px", color: "" }, toolTip: "Code", sortfield: true },
        { field: "subCategoryName", header: "Sub Category", style: { width: "100px", color: "" }, toolTip: "Reporting", sortfield: true },
        { field: "tasks", header: "Task", style: { width: "100px", color: "" }, toolTip: "Reporting", sortfield: true },
        { field: "actualHrs", header: "Actual Hours", style: { width: "100px", color: "" }, toolTip: "Reporting", sortfield: true },
        { field: "startDate", header: "Start Date", type: "datetime", style: { width: "100px", color: "" }, toolTip: "Modified", sortfield: true },
        { field: "endDate", type: "datetime", header: "End Date", style: { width: "100px", color: "" }, toolTip: "Modified", sortfield: true },
        { field: "startDateIst", header: "Start Date Ist", type: "datetime", style: { width: "100px", color: "" }, toolTip: "Modified", sortfield: true },
        { field: "endDateIst", type: "datetime", header: "End Date Ist", style: { width: "100px", color: "" }, toolTip: "Modified", sortfield: true },
      ];
    },

    async getTimeData(id) {
      this.loading = true;
      try {
        let res = await TimeSheetService.getGroupByData({
          userId: id,
          from_date: this.fromDate,
          to_date: this.toDate,
        });
        this.timeData = res;
        this.timeData.byDate = await commonService.getSort(res.byDate, "entry_date", "desc");
        if (this.timeData.byDate) this.sortGroupDataByStartDateDescending(this.timeData.byDate);
        this.timeData.byAccounts = await commonService.getSort(res.byAccounts, "entry_date", "desc");
        if (this.timeData.byAccounts) this.sortGroupDataByStartDateDescending(this.timeData.byAccounts);
      } finally {
        this.loading = false;
      }
    },

    sortGroupDataByStartDateDescending(data) {
      data.forEach((entry) => {
        if (entry.groupData) {
          const groupData = JSON.parse(entry.groupData);
          groupData.sort((a, b) => new Date(b.startDate) - new Date(a.startDate));
          entry.groupData = JSON.stringify(groupData);
        }
      });
    },

    formatDate(dateString, type) {
      let inputDate = new Date(dateString + " UTC");
      if (!inputDate) return "";
      return type == "date" ? new Date(inputDate).toLocaleDateString() : new Date(inputDate).toLocaleString();
    },

    exportIcon(type) {
      const map = { Excel: 'mdi-microsoft-excel', Pdf: 'mdi-file-pdf-box', Json: 'mdi-code-json' };
      return map[type] || 'mdi-file-outline';
    },
    exportIconColor(type) {
      const map = { Excel: '#16a34a', Pdf: '#dc2626', Json: '#d97706' };
      return map[type] || '#6366f1';
    },
    async exportFile(type) {
      let sourceData, column;
      if (this.tab === "tab-2") {
        sourceData = this.timeData.byAccounts.map((account) => {
          return JSON.parse(account.groupData).map((data) => ({ account_name: account.account_name, ...data }));
        }).flat();
        column = this.columns;
      } else {
        sourceData = JSON.parse(this.timeData.byDate[0].groupData);
        column = this.columns.filter(c => c.field !== "account_name");
      }
      await ExportService.export(sourceData, "TimeSheet", column, type);
    },

    getBgColors(name) {
      if (name == "Login") return "#e0fdf4";
      else if (name == "LogOut") return "#fce7f3";
      else if (name == "Meeting") return "#eff6ff";
      else if (name == "Break") return "#fff7ed";
      else return "#f5f3ff";
    },
    getColors(name) {
      if (name == "Login") return "#065f46";
      else if (name == "LogOut") return "#9d174d";
      else if (name == "Meeting") return "#1e40af";
      else if (name == "Break") return "#9a3412";
      else return "#4c1d95";
    },
    getBrdrColors(name) {
      if (name == "Login") return "#6ee7b7";
      else if (name == "LogOut") return "#f9a8d4";
      else if (name == "Meeting") return "#93c5fd";
      else if (name == "Break") return "#fdba74";
      else return "#c4b5fd";
    },
    getSubCatStyle(name) {
      const palettes = [
        { background: "#e0f2fe", color: "#0369a1" },
        { background: "#fef9c3", color: "#854d0e" },
        { background: "#f0fdf4", color: "#166534" },
        { background: "#fce7f3", color: "#9d174d" },
        { background: "#f5f3ff", color: "#4c1d95" },
        { background: "#fff7ed", color: "#9a3412" },
        { background: "#ecfdf5", color: "#065f46" },
        { background: "#eff6ff", color: "#1e40af" },
      ];
      let hash = 0;
      for (let i = 0; i < (name || "").length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
      return palettes[Math.abs(hash) % palettes.length];
    },
  },
};
</script>

<style>
/* ── WRAPPER ── */
.ts-wrapper {
  display: flex;
  flex-direction: column;
  height: 100%;
  background: #ffffff;
  border-radius: 14px;
  overflow: hidden;
}

/* ── HEADER ── */
.ts-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px 0;
  flex-shrink: 0;
}

.ts-header__left {
  display: flex;
  align-items: center;
  gap: 8px;
}

.ts-header__icon {
  width: 28px;
  height: 28px;
  border-radius: 8px;
  background: rgba(60, 90, 170, 0.08);
  display: flex;
  align-items: center;
  justify-content: center;
}

.ts-header__title {
  font-size: 13px;
  font-weight: 700;
  color: #0f172a;
  letter-spacing: -0.1px;
}

/* ── EXPORT BUTTON ── */
.ts-export-btn {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  height: 28px;
  padding: 0 10px;
  border-radius: 8px;
  border: 1px solid rgba(60, 90, 170, 0.2);
  background: linear-gradient(135deg, #eef2ff 0%, #e0e7ff 100%);
  color: #3C5AAA;
  font-size: 11px;
  font-weight: 700;
  cursor: pointer;
  letter-spacing: 0.2px;
  transition: transform 0.15s, box-shadow 0.15s, border-color 0.15s;
}
.ts-export-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(60, 90, 170, 0.18);
  border-color: rgba(60, 90, 170, 0.35);
}
.ts-export-btn:active {
  transform: translateY(0);
  box-shadow: none;
}

/* ── EXPORT DROPDOWN MENU ── */
.ts-export-menu {
  min-width: 180px;
  background: #ffffff;
  border-radius: 12px;
  border: 1px solid rgba(99, 102, 241, 0.12);
  box-shadow: 0 8px 28px rgba(99, 102, 241, 0.14), 0 2px 8px rgba(0,0,0,0.06);
  overflow: hidden;
  padding: 0;
}

.ts-export-menu__header {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 14px 8px;
  font-size: 10px;
  font-weight: 700;
  color: #94a3b8;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  background: linear-gradient(135deg, #fafbff 0%, #f5f3ff 100%);
}

.ts-export-menu__divider {
  height: 1px;
  background: linear-gradient(90deg, transparent, rgba(99, 102, 241, 0.12), transparent);
}

.ts-export-menu__item {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 9px 14px;
  border: none;
  background: transparent;
  cursor: pointer;
  transition: background 0.13s;
  text-align: left;
}
.ts-export-menu__item:hover {
  background: #f5f7ff;
}
.ts-export-menu__item:hover .ts-export-menu__arrow {
  opacity: 1;
  transform: translateX(2px);
}

.ts-export-menu__icon-wrap {
  width: 28px;
  height: 28px;
  border-radius: 8px;
  background: #f8fafc;
  border: 1px solid #e9edf5;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.ts-export-menu__label {
  flex: 1;
  font-size: 12px;
  font-weight: 600;
  color: #1e293b;
}

.ts-export-menu__arrow {
  flex-shrink: 0;
  opacity: 0;
  transition: opacity 0.13s, transform 0.13s;
}
.ts-tabs {
  display: flex;
  gap: 6px;
  padding: 12px 16px 0;
  flex-shrink: 0;
}

.ts-tab {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 5px 14px;
  font-size: 11px;
  font-weight: 600;
  color: #64748b;
  border: 1px solid transparent;
  background: #f1f5f9;
  border-radius: 20px;
  cursor: pointer;
  transition: all 0.18s ease;
}
.ts-tab:hover {
  color: #3C5AAA;
  background: rgba(60, 90, 170, 0.07);
}
.ts-tab--active {
  color: #3C5AAA;
  font-weight: 700;
  background: rgba(60, 90, 170, 0.1);
  border-color: rgba(60, 90, 170, 0.22);
}

/* ── CONTENT ── */
.ts-content {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 10px 0 10px;
}

/* ── SCROLL WRAP ── */
.ts-scroll-wrap {
  overflow-x: auto;
  padding: 0 14px 6px;
  scrollbar-width: none;
}

/* ── TABLE ── */
.ts-table {
  min-width: 1000px;
  width: 100%;
}

.ts-table--no-date {
  min-width: 790px;
}

/* ── THEAD ── */
.ts-thead {
  display: grid;
  grid-template-columns: 120px 130px 200px 120px 120px 1fr 100px;
  padding: 6px 12px;
  background: rgba(60, 90, 170, 0.04);
  border-radius: 8px;
  margin-bottom: 4px;
  position: sticky;
  top: 0;
  z-index: 1;
  border: 1px solid rgba(60, 90, 170, 0.07);
}

.ts-table--no-date .ts-thead {
  grid-template-columns: 120px 200px 120px 1fr 120px 100px;
}

.ts-th {
  font-size: 10px;
  font-weight: 700;
  color: #3C5AAA;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  padding: 2px 6px;
  opacity: 0.7;
}

/* ── GROUP ROW ── */
.ts-group-row {
  display: grid;
  grid-template-columns: 1fr 100px;
  align-items: center;
  /* min-width: 900px; */
  padding: 10px 12px 5px;
  font-size: 11px;
  font-weight: 700;
  color: #1e293b;
  border-left: 3px solid #3C5AAA;
  margin: 6px 0 2px;
  background: linear-gradient(90deg, rgba(60,90,170,0.04) 0%, transparent 100%);
  border-radius: 0 6px 6px 0;
}

.ts-group-row__left {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
}

.ts-group-total {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 3px;
  font-size: 10px;
  font-weight: 600;
  color: #3C5AAA;
  background: rgba(60, 90, 170, 0.07);
  padding: 2px 8px;
  border-radius: 10px;
  opacity: 0.85;
  white-space: nowrap;
  position: sticky;
  right: -14px;
}

/* ── DATA ROW ── */
.ts-row {
  display: grid;
  grid-template-columns: 120px 130px 200px 120px 120px 1fr 100px;
  align-items: center;
  padding: 7px 12px;
  border-radius: 8px;
  transition: background 0.14s;
  border-bottom: 1px solid #f8fafc;
}

.ts-table--no-date .ts-row {
  grid-template-columns: 120px 200px 120px 1fr 120px 100px;
}
.ts-row:hover {
  background: rgba(60, 90, 170, 0.03);
}

/* ── COLUMN WIDTHS ── */
.ts-col-type  { padding: 0 6px; }
.ts-col-date  { padding: 0 6px; }
.ts-col-time  { padding: 0 6px; }
.ts-col-ref   { padding: 0 6px; }
.ts-col-shif   { padding: 0 6px; }
.ts-col-cat   { padding: 0 6px; min-width: 0; overflow: hidden; }
.ts-col-dur {
  padding: 0 6px;
  text-align: right;
  position: sticky;
  right: -14px;
  background: #ffffff;
  z-index: 1;
  border-left: 1px solid #f1f5f9;
}
.ts-row:hover .ts-col-dur { background: rgba(60, 90, 170, 0.03); }
.ts-thead .ts-col-dur {
  background: rgba(60, 90, 170, 0.04);
  position: sticky;
  right: -14px;
  z-index: 2;
  border-left: 1px solid rgba(60, 90, 170, 0.07);
}

/* ── CHIPS & BADGES ── */

/* Wrapper for tooltip activator around chip */
.ts-chip-wrap {
  display: inline-block;
  max-width: 100%;
  overflow: hidden;
  vertical-align: middle;
}

.ts-chip {
  display: inline-flex;
  align-items: center;
  max-width: 100%;
  min-width: 0;
  font-size: 9px;
  font-weight: 700;
  padding: 3px 8px;
  border-radius: 6px;
  letter-spacing: 0.4px;
  overflow: hidden;
}

/* Text inside ts-chip and ts-sub-chip — ellipsis */
.ts-chip-label {
  display: block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
  max-width: 100px; /* cap type chip width */
}

.ts-ref-badge {
  font-size: 10px;
  font-weight: 600;
  color: #3C5AAA;
  background: rgba(60, 90, 170, 0.08);
  border: 1px solid rgba(60, 90, 170, 0.15);
  padding: 2px 7px;
  border-radius: 5px;
  white-space: nowrap;
}

/* Category cell wrapper for tooltip */
.ts-cat-wrap {
  display: flex;
  align-items: center;
  gap: 5px;
  min-width: 0;
  overflow: hidden;
  width: 100%;
}

.ts-sub-chip {
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  max-width: 120px;
  min-width: 0;
  font-size: 9px;
  font-weight: 600;
  padding: 2px 7px;
  border-radius: 4px;
  overflow: hidden;
}

/* ── CELL TEXT ── */
.ts-cell-time {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  color: #475569;
  font-weight: 500;
  white-space: nowrap;
}

.ts-arrow {
  color: #cbd5e1;
  font-size: 10px;
}

.ts-cell-text {
  display: block;
  font-size: 11px;
  color: #475569;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
  max-width: 100%;
}

/* Ensure grid cells containing text/chips don't overflow */
.ts-col-cat,
.ts-col-shif,
.ts-col-date {
  min-width: 0;
  overflow: hidden;
}

.ts-cell-muted {
  font-size: 10px;
  color: #cbd5e1;
}

.ts-dur-val {
  font-size: 12px;
  font-weight: 700;
  color: #1e293b;
}

.ts-dur-unit {
  font-size: 9px;
  font-weight: 400;
  color: #94a3b8;
  margin-left: 2px;
}

/* ── EMPTY STATE ── */
.ts-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 40px 20px;
}
.ts-empty__img {
  width: 110px;
  height: 110px;
  opacity: 0.7;
}
.ts-empty__title {
  font-size: 13px;
  font-weight: 700;
  color: #475569;
  margin-top: 4px;
}
.ts-empty__sub {
  font-size: 11px;
  color: #94a3b8;
  text-align: center;
  max-width: 220px;
  line-height: 1.6;
}

/* ── SCROLLBAR ── */
.ts-content::-webkit-scrollbar,
.ts-scroll-wrap::-webkit-scrollbar { width: 3px; height: 3px; }
.ts-content::-webkit-scrollbar-track,
.ts-scroll-wrap::-webkit-scrollbar-track { background: transparent; }
.ts-content::-webkit-scrollbar-thumb,
.ts-scroll-wrap::-webkit-scrollbar-thumb { background: rgba(60,90,170,0.15); border-radius: 4px; }
.ts-content::-webkit-scrollbar-thumb:hover,
.ts-scroll-wrap::-webkit-scrollbar-thumb:hover { background: rgba(60,90,170,0.3); }

/* ── LOADER ── */
.ts-loader {
  flex: 1;
  overflow: hidden;
  padding: 12px 14px;
}
.ts-shimmer-wrap {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.ts-shimmer {
  border-radius: 6px;
  background: linear-gradient(90deg, #f1f5f9 25%, #e8edf5 50%, #f1f5f9 75%);
  background-size: 200% 100%;
  animation: ts-shimmer-move 1.4s ease-in-out infinite;
}
.ts-shimmer--thead  { height: 32px; width: 100%; border-radius: 8px; margin-bottom: 4px; }
.ts-shimmer--group  { height: 12px; width: 35%; border-radius: 4px; margin: 6px 0 2px; }
.ts-shimmer--w50    { width: 50%; }
.ts-shimmer-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 0;
  border-bottom: 1px solid #f8fafc;
}
.ts-shimmer--cell   { height: 11px; flex-shrink: 0; }
.ts-shimmer--chip   { width: 64px; border-radius: 6px; }
.ts-shimmer--time   { width: 120px; }
.ts-shimmer--ref    { width: 80px; }
.ts-shimmer--cat    { flex: 1; }
.ts-shimmer--dur    { width: 50px; }
@keyframes ts-shimmer-move {
  0%   { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}
</style>
